/**
 * @file src/_modulos/prenota/tabela/layouts/config/content/ui-types.ts
 * @description Tipos simplificados para a tabela de pré-notas
 *
 * Princípios aplicados:
 * - KISS: Tipos simples e diretos
 * - YAGNI: Apenas tipos realmente necessários
 */

import type { ReactNode } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import type { ListaPrenotasParams, PrenotaItem } from "@apiNf/list";

/* -------------------------------------------------------------------------- */
/* TIPOS BASE                                                                  */
/* -------------------------------------------------------------------------- */

export type FilterValue =
  | string
  | number
  | string[]
  | { from: string; to: string };
export type AppliedFilters = Record<string, FilterValue>;
export type Sort<TId extends string = string> = { id: TId; desc: boolean };
export type PrenotaRow = PrenotaItem;

/* -------------------------------------------------------------------------- */
/* CONTRATOS DE API                                                            */
/* -------------------------------------------------------------------------- */

export interface FetchPrenotasApiParams extends ListaPrenotasParams {
  filials?: string[];
  searchTerm?: string;
  sorting?: Sort[];
  filters?: Record<string, unknown>;
}

export interface FetchPrenotasResponse {
  success: boolean;
  data: PrenotaRow[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
}

export type UsePrenotasOptions = Omit<FetchPrenotasApiParams, "filters">;

/* -------------------------------------------------------------------------- */
/* TIPOS DE UI                                                                 */
/* -------------------------------------------------------------------------- */

export interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  isLoading?: boolean;
  enableSorting?: boolean;
  className?: string;
  children?: ReactNode;
}

export interface FilterDefinition {
  id: string;
  column: string;
  operator: string;
  value: string | number | (string | number)[];
  label: string;
}

export interface ActiveFilter {
  id: string;
  column: string;
  operator: string;
  value: any;
  label: string;
}

/* -------------------------------------------------------------------------- */
/* RE-EXPORTS                                                                  */
/* -------------------------------------------------------------------------- */

export type { ListaPrenotasParams, PrenotaItem } from "@apiNf/list";
